/**
 * События использования для отчёта владельцу (admin.skokadeneg.ru).
 *
 * Что открывают, что делают, где ломается — именами экранов и действий.
 * **Сумм, названий трат, категорий, заметок и текстов сюда не передаём
 * никогда**: свойства события — только перечисления из кода (`kind`, `step`,
 * `method`) и числа вроде длительности. Сервер дополнительно режет всё,
 * похожее на деньги и тексты (`apps/api/src/events.mjs`).
 *
 * Отправка — фоном, пачками, без ожидания ответа. Ошибка отправки не
 * показывается человеку и не мешает работе: отчёт важен, но не важнее
 * записи траты.
 */
import { useEffect } from 'react';

/** Имя события: словарь общий с сервером (`apps/api/src/report.mjs`). */
export type EventName =
  // где человек
  | 'screen_view' | 'screen_time' | 'section_switch' | 'sheet_open'
  // что делает
  | 'op_open' | 'op_saved' | 'op_abandoned' | 'op_edited' | 'op_deleted'
  | 'check_purchase' | 'ask_sent' | 'payment_settled'
  | 'account_added' | 'income_added' | 'obligation_added' | 'loan_added' | 'goal_added' | 'goal_funded'
  | 'balance_adjusted' | 'csv_exported' | 'bank_shots'
  | 'auth_register' | 'auth_login' | 'auth_logout' | 'account_deleted'
  // откуда пришли: ссылка с сайта skokadeneg.ru или с метками
  | 'came_from'
  | 'plans_opened' | 'pro_wall' | 'trial_started' | 'pay_pressed'
  // онбординг
  | 'onb_start' | 'onb_step' | 'onb_leave' | 'onb_sheet' | 'onb_skip_all' | 'onb_restored' | 'onb_done' | 'onb_analysis'
  // проблемы
  | 'error' | 'api_error' | 'ai_failed' | 'sync_failed' | 'sync_conflict';

type Props = Record<string, string | number | boolean>;
interface Queued { name: EventName; at: number; screen: string | null; props?: Props }

const DEVICE_KEY = 'skoka.device';
const FLUSH_MS = 10_000;
const FLUSH_AT = 20;
const KEEP = 300;

const rid = (): string => {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** Номер устройства: случайный, живёт в браузере. Без хранилища — на время открытия. */
const device = ((): string => {
  try {
    const saved = localStorage.getItem(DEVICE_KEY);
    if (saved && /^[a-f0-9]{24}$/.test(saved)) return saved;
    const fresh = rid();
    localStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    return rid();
  }
})();
/** Сессия — одно открытие приложения. */
const session = rid();

/** Где открыто: телефон или компьютер, какой браузер, установленное ли приложение. */
export function appContext(ua = navigator.userAgent) {
  const platform = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ? 'ios'
    : /Android/.test(ua) ? 'android'
      : /Mac/.test(ua) ? 'mac'
        : /Windows/.test(ua) ? 'windows'
          : /Linux/.test(ua) ? 'linux' : 'other';
  const browser = /YaBrowser/.test(ua) ? 'yandex'
    : /SamsungBrowser/.test(ua) ? 'samsung'
      : /OPR\/|Opera/.test(ua) ? 'opera'
        : /Edg\//.test(ua) ? 'edge'
          : /Firefox|FxiOS/.test(ua) ? 'firefox'
            : /Chrome|CriOS/.test(ua) ? 'chrome'
              : /Safari/.test(ua) ? 'safari' : 'other';
  let standalone = false;
  try {
    standalone = matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch { /* старый браузер */ }
  return { version: import.meta.env.VITE_APP_VERSION ?? 'dev', platform, browser, standalone };
}

let queue: Queued[] = [];
let screen: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
/** В проверках и в разработке без сервера события копятся, но не уходят. */
const enabled = typeof window !== 'undefined' && import.meta.env.MODE !== 'test';

/*
  Активное время — пока приложение на экране и человек что-то делал за
  последнюю минуту. Открытая и забытая вкладка иначе насчитала бы часы, а
  отчёт сделал бы вывод «долго пишут операцию», хотя человек ушёл пить чай.
*/
const IDLE_MS = 60_000;
let activeMs = 0;
let lastTick = Date.now();
let lastInput = Date.now();
const visible = (): boolean => typeof document === 'undefined' || document.visibilityState === 'visible';
function tick(): void {
  const now = Date.now();
  if (visible() && now - lastInput < IDLE_MS) activeMs += Math.min(now - lastTick, IDLE_MS);
  lastTick = now;
}
/** Сколько активного времени набежало с запуска — для «сколько ушло на запись». */
export function activeTime(): number { tick(); return activeMs; }

/** Время на текущем экране: отправляется при уходе с него или при сворачивании. */
let screenFrom = 0;
function flushScreenTime(): void {
  const now = activeTime();
  const ms = now - screenFrom;
  screenFrom = now;
  if (screen && ms >= 1000) track('screen_time', { ms: Math.round(ms) });
}

/** Текущий экран: к нему привязываются события и сбои. */
export function setScreen(next: string): void {
  if (next === screen) return;
  flushScreenTime();
  screen = next;
  track('screen_view');
}
export const currentScreen = (): string | null => screen;

export function track(name: EventName, props?: Props): void {
  if (!enabled) return;
  queue.push({ name, at: Date.now(), screen, ...(props ? { props } : {}) });
  if (queue.length > KEEP) queue = queue.slice(-KEEP);
  if (queue.length >= FLUSH_AT) void flush();
  else if (!timer) timer = setTimeout(() => { timer = null; void flush(); }, FLUSH_MS);
}

function payload(events: Queued[]): string {
  return JSON.stringify({ device, session, app: appContext(), events });
}

/** Отправить накопленное. `beacon` — когда страница уходит: обычный запрос не успеет. */
export async function flush(beacon = false): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, 100);
  const body = payload(batch);
  try {
    if (beacon && navigator.sendBeacon) {
      if (navigator.sendBeacon('/api/events', new Blob([body], { type: 'application/json' }))) return;
    }
    const res = await fetch('/api/events', {
      method: 'POST', body, credentials: 'same-origin', keepalive: true,
      headers: { 'content-type': 'application/json' },
    });
    if (!res.ok && res.status >= 500) throw new Error(String(res.status));
  } catch {
    // Нет сети — вернуть в очередь и попробовать со следующей пачкой.
    queue = [...batch, ...queue].slice(-KEEP);
  }
}

/** Адрес запроса без номеров и параметров: «/state/42?x» → «/state/:id». */
export const apiPath = (path: string): string =>
  path.split('?')[0]!.replace(/\/[0-9a-f-]{8,}|\/\d+/gi, '/:id').slice(0, 60);

/** Задача ИИ по адресу: по ней в отчёте видно, что именно не разобралось. */
export function aiTask(path: string): string | null {
  const p = apiPath(path);
  // «Настроен ли ИИ» — служебная проверка, а не разбор: её обрыв — просто нет связи.
  if (!p.startsWith('/ai/') || p === '/ai/status') return null;
  if (/parse-expense|phrase/.test(p)) return 'phrase';
  if (/speech|stt/.test(p)) return 'speech';
  if (/statement/.test(p)) return 'statement';
  if (/document|receipt-file|pdf/.test(p)) return 'document';
  if (/receipt/.test(p)) return 'receipt';
  if (/basket|classify|categorize/.test(p)) return 'basket';
  if (/advisor|analy|ask/.test(p)) return 'advisor';
  if (/review/.test(p)) return 'review';
  if (/chart/.test(p)) return 'chart';
  return p.slice(4, 30);
}

/** Сообщения браузера, которые сбоем не являются. */
const BENIGN = /ResizeObserver loop/;

let started = false;
/**
 * Подключить отлов сбоев и отправку при уходе. Один раз на запуск.
 *
 * Повтор одного и того же сбоя в одну сессию шлётся один раз: зациклившийся
 * обработчик иначе заполнил бы очередь одинаковыми строками.
 */
export function startTracking(): void {
  if (started || !enabled) return;
  started = true;
  cameFrom();
  const seen = new Set<string>();
  const report = (source: string, err: unknown, fallback: string) => {
    const e = err instanceof Error ? err : null;
    const message = (e?.message ?? fallback).slice(0, 300);
    // Служебное предупреждение браузера: человек его не видит, и ничего не ломается.
    if (BENIGN.test(message)) return;
    const key = `${source}|${message}`;
    if (seen.has(key)) return;
    seen.add(key);
    track('error', { source, message, stack: (e?.stack ?? '').slice(0, 2000) });
  };
  addEventListener('error', (ev) => {
    // Не загрузился скрипт или стиль — это не сбой кода, а сеть: его ловит экран загрузки.
    if (!(ev instanceof ErrorEvent)) return;
    report('js', ev.error, ev.message || 'Ошибка без текста');
  });
  addEventListener('unhandledrejection', (ev) => report('promise', ev.reason, String(ev.reason ?? 'Отклонённое обещание')));
  // Активность: любое касание, клавиша, прокрутка. Тик раз в 5 секунд
  // досчитывает время, пока человек на экране.
  const touched = () => { tick(); lastInput = Date.now(); };
  for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
    addEventListener(type, touched, { passive: true, capture: true });
  }
  setInterval(tick, 5_000);
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { flushScreenTime(); void flush(true); }
    else { lastTick = Date.now(); lastInput = Date.now(); }
  });
  addEventListener('pagehide', () => { flushScreenTime(); void flush(true); });
}

/**
 * Откуда пришли: ссылка с сайта skokadeneg.ru («Начать бесплатно» несёт номер
 * посетителя сайта `sd` и метки) или любая ссылка с utm-метками. Событие
 * `came_from` связывает заход на сайт с регистрацией — вкладка «Сайт» отчёта
 * (`apps/api/src/site-report.mjs`). Метки убираются из адреса сразу: перезагрузка
 * и ярлык на экране «Домой» не засчитают приход второй раз.
 */
export const CAME_KEYS = ['sd', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;
const CAME_PROP: Record<(typeof CAME_KEYS)[number], string> = {
  sd: 'sd', utm_source: 'src', utm_medium: 'med', utm_campaign: 'camp', utm_content: 'cont',
};
export function cameFromProps(search: string): Props | null {
  const q = new URLSearchParams(search);
  const props: Props = {};
  for (const k of CAME_KEYS) {
    const v = q.get(k)?.trim().toLowerCase().slice(0, 64);
    if (v) props[CAME_PROP[k]] = v;
  }
  return Object.keys(props).length ? props : null;
}
function cameFrom(): void {
  if (typeof location === 'undefined') return;
  const props = cameFromProps(location.search);
  if (!props) return;
  track('came_from', props);
  try {
    const url = new URL(location.href);
    for (const k of CAME_KEYS) url.searchParams.delete(k);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  } catch { /* адрес не поменять — не беда */ }
}

/** Отметить экран при показе: `<ScreenMark name="welcome" />` рядом с ним. */
export function ScreenMark({ name }: { name: string }): null {
  useEffect(() => { setScreen(name); }, [name]);
  return null;
}

/** Ключ листа приложения: строка как есть, у объекта — его первое поле. */
export const sheetKey = (sheet: unknown): string | null =>
  sheet === null || sheet === undefined ? null
    : typeof sheet === 'string' ? sheet
      : typeof sheet === 'object' ? (Object.keys(sheet as object)[0] ?? 'sheet') : null;
